import { Breadcrumb, Col, Input, Popconfirm, Row, Space, Table, Tooltip } from "antd";
import "antd/dist/antd.min.css";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getRequest, postRequest } from "../../../axios";
import ButtonField from "../../../common/htmlComponents/ButtonField";
import { ReactComponent as ArchiveRestore } from "../../../image/ArchiveRestore.svg";
import SearchIcon from "../../../image/SearchIcon.svg";
import "../../../styles/Main.scss";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../../utils/Notifications";

const { Search } = Input;

const ArchivedMaterialList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [archivedMaterialList, setArchivedMaterialList] = useState([]);
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchText, setSearchText] = useState("");
  //State initiaillisation
  const [state, setState] = useState({
    page: 1,
    ordering: "-id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  // Fetch material archive list when the component mounts or state changes
  useEffect(() => {
    const getMaterialArchiveList = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest("material-master-archive/", state);
        if (apiResponse.data.results.success) {
          setArchivedMaterialList(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archived_material"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getMaterialArchiveList();
  }, [state, t]);

  // Handle table changes like pagination, sorting, etc.
  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
    setSelectedRow(selectedRow);
  };

  // Convert sort field and order to sorting enum value for API request
  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "-id";

    if (sortField === "code" && sortOrder === "ascend") {
      sortingEnumKey = "code";
    }
    if (sortField === "code" && sortOrder === "descend") {
      sortingEnumKey = "-code";
    }
    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree__name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree__name";
    }

    if (sortField === "material_type_name" && sortOrder === "ascend") {
      sortingEnumKey = "material_type__type_name";
    }
    if (sortField === "material_type_name" && sortOrder === "descend") {
      sortingEnumKey = "-material_type__type_name";
    }
    if (sortField === "short_description" && sortOrder === "ascend") {
      sortingEnumKey = "short_description";
    }
    if (sortField === "short_description" && sortOrder === "descend") {
      sortingEnumKey = "-short_description";
    }
    if (sortField === "long_description" && sortOrder === "ascend") {
      sortingEnumKey = "long_description";
    }
    if (sortField === "long_description" && sortOrder === "descend") {
      sortingEnumKey = "-long_description";
    }

    if (sortField === "unit_of_measurement" && sortOrder === "ascend") {
      sortingEnumKey = "unit_of_measurement";
    }
    if (sortField === "unit_of_measurement" && sortOrder === "descend") {
      sortingEnumKey = "-unit_of_measurement";
    }

    if (sortField === "image" && sortOrder === "ascend") {
      sortingEnumKey = "image";
    }
    if (sortField === "image" && sortOrder === "descend") {
      sortingEnumKey = "-image";
    }

    return sortingEnumKey;
  };

  // Delayed search callback function
  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };

  // Function to handle search input and update state
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };


// Configuration for row selection in the table
  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, record.id]
        : selectedRow.filter((key) => key !== record.id);
      setSelectedRow(updatedSelectedRowKeys);
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const updatedSelectedRowKeys = selected
        ? [...selectedRow, ...changeRows.map((row) => row.id)]
        : selectedRow.filter(
            (key) => !changeRows.find((row) => row.id === key)
          );
      setSelectedRow(updatedSelectedRowKeys);
    },
    selectedRowKeys: selectedRow,
  };
  
  
// Debounce function to delay search requests
  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  // Function to restore selected archived items
  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }
    try {
      let apiResponse = await postRequest("material-master-archive/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("material_restore_successfully6"));
        setSelectedRow([]);
        navigate("/material");
      } else {
        ErrorNotificationMsg(t("error_in_restore_material"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };
  const openImageInNewTab = (imageSrc) => {
    window.open(imageSrc, "_blank");
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      sorter: true,
      width: 200,
    },
    {
      title: "Material Type",
      dataIndex: "material_type_name",
      key: "material_type_name",
      sorter: true,
      width: 200,
    },

    {
      title: "Short Description",
      dataIndex: "short_description",
      key: "short_description",
      sorter: true,
      width: 200,
      render: (dataIndex) => (
        <Tooltip
          color={colors}
          placement="left"
          title={<span style={{ color: "#fff" }}>{dataIndex}</span>}
        >
          <div
            style={{
              maxWidth: "400px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataIndex}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "UoM.",
      dataIndex: "unit_of_measurement",
      key: "unit_of_measurement",
      sorter: true,
      width: 200,
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 30,
      render: (text, record) => (
        <Space>
          <Tooltip
            color={colors}
            placement="right"
            title={
              <span style={{ color: "#fff" }}>
                Restore
              </span>
            }
          >
            <Popconfirm
              className="action"
              title={t("pop_restore_title")}
              okText={t("pop_ok")}
              cancelText={t("pop_cancel")}
              onConfirm={() => handleRestore(record.id)}
            >
              <ArchiveRestore className="ArchiverestorImg" />{" "}
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">

            <h3>{t("archived_material")}</h3>


            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/material">{t("material")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("archiveList")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
              <Search
  value={searchText}
  onChange={(e) => {
    const value = e.target.value;
    setSearchText(value);
    debounceLoadData(value, onSearch);
  }}
  allowClear
  onSearch={(value) => {
    setSearchText(value);
    onSearch(value);
  }}
  prefix={
    <img
      src={SearchIcon}
      alt="SearchIcon"
      className="SearchIcon"
      width={22}
    />
  }
  placeholder={t("pl_search_list")}
  style={{ width: 250 }}
/>
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label="Restore"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

       <div className="stepscontentwrap">
        <div className="stepsContent">
      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body" style={{padding:'0'}}>
          <Row className="tblheader">
            <Col
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={12}
              className="tbl-heading"
            >
            </Col>
          </Row>
<div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={archivedMaterialList}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
              showTotal: (totalCount) => `Total ${totalCount} items`,
            }}
            onChange={handleTableChange}
          />
          </div>
          <div className="FormFooter Container">
            <ButtonField
              className="SaveGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("pl_back_to_list")}
              onClick={() =>
                navigate(
                  `/material`
                )
              }
            />
          </div>
        </div>
      </section>
      </div></div>
    </>
  );
};

export default ArchivedMaterialList;
